import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '@pilot-hub/ui'
import { api } from '@pilot-hub/api-client'
import { AlertTriangle, ClipboardCheck, PlaneLanding, Power } from 'lucide-react'
import { useAufforderungen, aufforderungenNeuLaden, type VorflugFehltAuff } from '../hooks/useAufforderungen'
import { ChecklisteDialog } from './ChecklisteDialog'
import { AufforderungsGruppe } from './AufforderungsGruppe'
import { popupKey, popupWegMerken } from './VorflugPopup'

// Banner im Pilot-Modul für Flüge, die die DJI Cloud API erkennt:
//  Pilot-Modus:    Drohne eingeschaltet -> Vorflugkontrolle; Landung erkannt -> Nachflug-Checkliste (öffnet sich automatisch);
//                  Abheben ohne Vorflugkontrolle -> nachtragen
//  Co-Pilot-Modus: Vorflugkontrolle fehlt -> nachtragen; Drohne im Rückflug/Landeanflug -> Vorbereitung der Landung
// (die Checklisten des Co-Piloten stehen auf der Seite "Dashboard", rechte Spalte)

const GESEHEN_KEY = 'pilot-hub-aufforderungen-gesehen'
const AUTO_NACHFLUG_KEY = 'pilot-hub-auto-nachflug'
const ausgeblendetKey = (id: number, seit: string | null) => `${id}@${seit ?? ''}`

function lesen(key: string): string[] {
  try { return JSON.parse(sessionStorage.getItem(key) || '[]') } catch { return [] }
}
function merken(key: string, wert: string) {
  try { sessionStorage.setItem(key, JSON.stringify([...lesen(key), wert].slice(-50))) } catch { /* egal */ }
}

function signal() {
  try { navigator.vibrate?.([300, 120, 300]) } catch { /* egal */ }
  try {
    const AC = window.AudioContext || (window as any).webkitAudioContext
    if (!AC) return
    const ac = new AC()
    const o = ac.createOscillator(); const g = ac.createGain()
    o.frequency.value = 880; g.gain.value = 0.15
    o.connect(g); g.connect(ac.destination)
    o.start(); o.stop(ac.currentTime + 0.35)
    window.setTimeout(() => { void ac.close() }, 600)
  } catch { /* Ton ohne vorherige Berührung nicht erlaubt */ }
}

const kasten = 'rounded-xl border px-3 py-2'

export function CloudAufforderungen({ pilotMode }: { pilotMode: 'PILOT' | 'CO_PILOT' }) {
  const { daten, geladen } = useAufforderungen()
  const navigate = useNavigate()
  const location = useLocation()
  const [dialog, setDialog] = useState<VorflugFehltAuff | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [aus, setAus] = useState<string[]>(() => lesen('pilot-hub-vorflug-ausgeblendet'))
  const [hinweisAus, setHinweisAus] = useState<string[]>(() => lesen('pilot-hub-vorflug-hinweis-aus'))
  const initial = useRef(true)

  const istPilot = pilotMode === 'PILOT'
  const vorflug = istPilot ? daten.vorflug.filter(v => !aus.includes(ausgeblendetKey(v.drohne_id, v.eingeschaltet_am)) || v.status === 'VON_MIR') : []
  const nachflug = istPilot ? daten.nachflug : []
  const fehlt = daten.vorflug_fehlt.filter(f => f.rolle === (istPilot ? 'PILOT' : 'CO_PILOT'))
  const landung = istPilot ? [] : daten.landung
  // Drohne an, aber keine Vorflug-Aufforderung (z. B. kein Einsatz / nicht zugeordnet): sichtbar sagen, warum
  const hinweise = istPilot ? daten.vorflug_hinweise.filter(h => !hinweisAus.includes(h.key)) : []

  // Neue Aufforderung: Ton und Vibration (nicht beim ersten Laden der Seite), Nachflug öffnet sich automatisch
  const schluessel = [
    ...vorflug.filter(v => v.status === 'OFFEN').map(v => `v:${v.drohne_id}@${v.eingeschaltet_am}`),
    ...nachflug.map(n => `n:${n.flug_id}`),
    ...fehlt.map(f => `f:${f.flug_id}`),
    ...landung.map(l => `l:${l.flug_id}`),
  ]
  useEffect(() => {
    if (!geladen) return
    const gesehen = lesen(GESEHEN_KEY)
    const neu = schluessel.filter(k => !gesehen.includes(k))
    neu.forEach(k => merken(GESEHEN_KEY, k))
    if (neu.length && !initial.current) signal()
    initial.current = false
    if (istPilot) {
      const n = nachflug.find(x => !lesen(AUTO_NACHFLUG_KEY).includes(String(x.flug_id)))
      if (n) { merken(AUTO_NACHFLUG_KEY, String(n.flug_id)); nachflugOeffnen(n.flug_id, n.einsatz_id) }
    }
  }, [geladen, schluessel.join('|')]) // eslint-disable-line react-hooks/exhaustive-deps

  function nachflugOeffnen(flugId: number, einsatzId: number) {
    navigate('/flug-erfassen', { state: { auftrag: { typ: 'nachflug', flugId, einsatzId } } })
  }
  async function vorflugStarten(drohneId: number, einsatzId: number, seit?: string | null) {
    setFehler(null)
    popupWegMerken(popupKey(drohneId, seit ?? null))   // Weg über das Banner: das Pop-up soll nicht zusätzlich aufgehen
    navigate('/flug-erfassen', { state: { auftrag: { typ: 'vorflug', drohneId, einsatzId } } })
  }
  function ausblenden(drohneId: number, seit: string | null) {
    const k = ausgeblendetKey(drohneId, seit)
    popupWegMerken(popupKey(drohneId, seit))
    merken('pilot-hub-vorflug-ausgeblendet', k)
    setAus(a => [...a, k])
    void api.post(`/pilot/vorflug/${drohneId}/freigeben`).catch(() => {})
  }
  function zuChecklisten() {
    if (location.pathname !== '/checklisten') navigate('/checklisten')
    window.setTimeout(() => document.getElementById('flug-checklisten')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150)
  }

  if (!vorflug.length && !nachflug.length && !fehlt.length && !landung.length && !hinweise.length) return null
  return (
    <div className="mb-3 space-y-2" data-testid="cloud-aufforderungen">
      {fehler && <div className="text-sm text-red-200">{fehler}</div>}

      {landung.map(l => (
        <div key={`l${l.flug_id}`} className={`${kasten} border-red-500/50 bg-red-500/10`} data-testid="aufforderung-landung">
          <div className="flex flex-wrap items-center gap-2">
            <ClipboardCheck className="h-5 w-5 shrink-0 text-red-300" />
            <div className="min-w-0 flex-1 basis-48 text-sm text-red-100">
              <div className="font-semibold">Vorbereitung der Landung</div>
              <div className="truncate text-xs text-red-200/80">{l.drohne ?? 'Drohne'} · {l.modus}</div>
            </div>
            <Button size="sm" className="!min-h-[48px] px-4" onClick={zuChecklisten}>Checkliste öffnen</Button>
          </div>
        </div>
      ))}

      {hinweise.map(h => (
        <div key={h.key} className={`${kasten} border-amber-500/40 bg-amber-500/10`} data-testid="aufforderung-vorflug-hinweis" data-grund={h.grund}>
          <div className="flex flex-wrap items-center gap-2">
            <AlertTriangle className="h-5 w-5 shrink-0 text-amber-300" />
            <div className="min-w-0 flex-1 basis-48 text-sm text-amber-100">{h.text}</div>
            <button type="button" className="min-h-[48px] rounded-lg px-3 text-sm text-amber-100 hover:bg-amber-500/10"
              onClick={() => { merken('pilot-hub-vorflug-hinweis-aus', h.key); setHinweisAus(a => [...a, h.key]) }}>Verstanden</button>
          </div>
        </div>
      ))}

      {vorflug.map(v => (
        <div key={`v${v.drohne_id}`} className={`${kasten} ${v.status === 'IN_ARBEIT' || v.status === 'UEBERNOMMEN' ? 'border-dark-border bg-dark-bg-tertiary/60' : 'border-sky-500/40 bg-sky-500/10'}`} data-testid="aufforderung-vorflug">
          <div className="flex flex-wrap items-center gap-2">
            <Power className="h-5 w-5 shrink-0 text-sky-300" />
            <div className="min-w-0 flex-1 basis-48 text-sm text-dark-text">
              <div className="font-semibold">Drohne {v.drohne} eingeschaltet – Vorflugkontrolle</div>
              <div className="truncate text-xs text-dark-text-muted">
                {v.status === 'UEBERNOMMEN' ? `Übernommen von ${v.von ?? 'einem Piloten'} – Flug gestartet`
                  : v.status === 'IN_ARBEIT' ? `${v.von ?? 'Ein Pilot'} macht gerade die Vorflugkontrolle`
                  : v.status === 'VON_MIR' ? 'Du hast die Vorflugkontrolle übernommen'
                  : v.mehrere_empfaenger ? 'Wer zuerst startet, übernimmt' : v.einsatz_titel}
              </div>
            </div>
            {(v.status === 'OFFEN' || v.status === 'VON_MIR') && (
              <>
                <Button size="sm" className="!min-h-[48px] px-4" onClick={() => vorflugStarten(v.drohne_id, v.einsatz_id, v.eingeschaltet_am)}>Vorflugkontrolle starten</Button>
                {v.status === 'OFFEN' && <button type="button" className="min-h-[48px] rounded-lg px-3 text-sm text-dark-text-muted hover:bg-dark-bg-tertiary" onClick={() => ausblenden(v.drohne_id, v.eingeschaltet_am)}>Später</button>}
              </>
            )}
          </div>
        </div>
      ))}

      {nachflug.map(n => (
        <div key={`n${n.flug_id}`} className={`${kasten} border-emerald-500/40 bg-emerald-500/10`} data-testid="aufforderung-nachflug">
          <div className="flex flex-wrap items-center gap-2">
            <PlaneLanding className="h-5 w-5 shrink-0 text-emerald-300" />
            <div className="min-w-0 flex-1 basis-48 text-sm text-dark-text">
              <div className="font-semibold">Gelandet – Nachflug offen{n.drohne ? ` · ${n.drohne}` : ''}</div>
              <div className="truncate text-xs text-dark-text-muted">
                {n.wieder_in_der_luft ? 'Die Drohne ist schon wieder in der Luft – der Nachflug des letzten Flugs ist noch offen' : `${n.flug_nr ?? ''} · Nachflug-Checkliste ausfüllen`}
              </div>
            </div>
            <Button size="sm" className="!min-h-[48px] px-4" onClick={() => nachflugOeffnen(n.flug_id, n.einsatz_id)}>Nachflug-Checkliste öffnen</Button>
          </div>
        </div>
      ))}

      <AufforderungsGruppe id={istPilot ? 'fehlt-pilot' : 'fehlt-copilot'} anzahl={fehlt.length} testid="gruppe-vorflug-fehlt"
        titel={`${fehlt.length} Vorflugkontrollen fehlen`} farbe="border-amber-500/40 bg-amber-500/10 text-amber-100"
        icon={<AlertTriangle className="h-5 w-5 shrink-0 text-amber-300" />}>
      {fehlt.map(f => (
        <div key={`f${f.flug_id}`} className={`${kasten} border-amber-500/40 bg-amber-500/10`} data-testid="aufforderung-vorflug-fehlt">
          <div className="flex flex-wrap items-center gap-2">
            <AlertTriangle className="h-5 w-5 shrink-0 text-amber-300" />
            <div className="min-w-0 flex-1 basis-48 text-sm text-amber-100">
              <div className="font-semibold">Vorflugkontrolle fehlt – nachtragen</div>
              <div className="truncate text-xs text-amber-200/80">{f.drohne ?? 'Drohne'} · {f.flug_nr ?? ''}{f.laeuft ? ' · Flug läuft' : ''}</div>
            </div>
            {istPilot
              ? <Button size="sm" variant="secondary" className="!min-h-[48px] px-4" onClick={() => setDialog(f)}>Nachtragen</Button>
              : <Button size="sm" variant="secondary" className="!min-h-[48px] px-4" onClick={zuChecklisten}>Zur Checkliste</Button>}
          </div>
        </div>
      ))}
      </AufforderungsGruppe>

      {dialog && (
        <ChecklisteDialog einsatzId={dialog.einsatz_id} instanzId={dialog.instanz_id}
          titel="Vorflugkontrolle nachtragen" hinweis="Der Flug läuft schon. Die Checkliste wird als nachgetragen gekennzeichnet."
          onClose={() => { setDialog(null); aufforderungenNeuLaden() }} />
      )}
    </div>
  )
}
